import { getAuthHeaders } from '../utils/authHeaders';
import { createHttpRequestError } from '../utils/chatNetworkErrors';
import { getCurrentGatewayUrl } from '../utils/environmentDetection';
import type {
  ChatThreadRedoRestoreResponse,
  ChatThreadRestoreCheckpointResponse,
  ChatThreadRewindResponse,
  TerraUIMessage,
} from '@terra/shared-dto-chat-types';

const CHAT_HISTORY_PAGE_SIZE = 50;
const CHAT_HISTORY_REWIND_TIMEOUT_MS = 30_000;

export type ChatHistoryResponse = {
  threadId: string;
  history: TerraUIMessage[];
};

export type PaginatedChatHistoryResponse = ChatHistoryResponse & {
  nextCursor: number | null;
  hasMore: boolean;
};

export async function loadChatHistoryPage({
  threadId,
  limit = CHAT_HISTORY_PAGE_SIZE,
  cursor,
}: {
  threadId: string;
  limit?: number;
  cursor?: number;
}): Promise<PaginatedChatHistoryResponse | null> {
  const authHeaders = await getAuthHeaders();
  const query = new URLSearchParams({ limit: String(limit) });

  if (cursor !== undefined) {
    query.set('cursor', String(cursor));
  }

  const response = await fetch(
    `${getCurrentGatewayUrl()}/chat/chat-history/thread/${threadId}?${query.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: authHeaders,
    },
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw createHttpRequestError({
      message: `Chat history request failed with status ${response.status}`,
      statusCode: response.status,
    });
  }

  const data = (await response.json()) as PaginatedChatHistoryResponse;

  if (data?.history && !Array.isArray(data.history)) {
    console.error(
      '[loadChatHistoryPage] Loaded history is not an array:',
      data,
    );

    throw new Error('Invalid history format from server');
  }

  return data;
}

export async function loadChatHistory({
  threadId,
}: {
  threadId: string;
}): Promise<ChatHistoryResponse | null> {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(
    `${getCurrentGatewayUrl()}/chat/chat-history/thread/${threadId}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: authHeaders,
    },
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw createHttpRequestError({
      message: `Chat history request failed with status ${response.status}`,
      statusCode: response.status,
    });
  }

  const data = await response.json();

  if (data && data.history) {
    if (!Array.isArray(data.history)) {
      console.error('[loadChatHistory] Loaded history is not an array:', data);

      throw new Error('Invalid history format from server');
    }

    console.debug('[loadChatHistory] Loaded history:', {
      messageCount: data.history.length,
      firstMessageHasParts:
        data.history.length > 0 && Array.isArray(data.history[0]?.parts),
    });
  }

  return data;
}

export async function deleteChatHistory({
  threadId,
}: {
  threadId: string;
}): Promise<{ success: boolean }> {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(
    `${getCurrentGatewayUrl()}/chat/chat-history/thread/${threadId}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: authHeaders,
    },
  );

  if (!response.ok) {
    throw new Error('Failed to delete chat history');
  }

  return response.json();
}

export async function rewindChatHistory({
  threadId,
  fromMessageId,
  revertFiles,
}: {
  threadId: string;
  fromMessageId: string;
  revertFiles: boolean;
}): Promise<ChatThreadRewindResponse> {
  return postCopilotThreadAction({
    threadId,
    action: 'rewind',
    body: { fromMessageId, revertFiles },
    failureLabel: 'Chat history rewind',
  });
}

export async function restoreChatCheckpoint({
  threadId,
  messageId,
}: {
  threadId: string;
  messageId: string;
}): Promise<ChatThreadRestoreCheckpointResponse> {
  return postCopilotThreadAction({
    threadId,
    action: 'restore-checkpoint',
    body: { messageId },
    failureLabel: 'Chat checkpoint restore',
  });
}

export async function redoChatCheckpointRestore({
  threadId,
}: {
  threadId: string;
}): Promise<ChatThreadRedoRestoreResponse> {
  return postCopilotThreadAction({
    threadId,
    action: 'redo-restore',
    body: {},
    failureLabel: 'Chat checkpoint redo',
  });
}

async function postCopilotThreadAction<TResponse>({
  threadId,
  action,
  body,
  failureLabel,
}: {
  threadId: string;
  action: 'rewind' | 'restore-checkpoint' | 'redo-restore';
  body: Record<string, unknown>;
  failureLabel: string;
}): Promise<TResponse> {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(
    `${getCurrentGatewayUrl()}/chat/graphs/co-pilot/thread/${threadId}/${action}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(CHAT_HISTORY_REWIND_TIMEOUT_MS),
    },
  ).catch((error: unknown) => {
    if (error instanceof DOMException && error.name === 'TimeoutError') {
      throw new Error(`${failureLabel} timed out`);
    }

    throw error;
  });

  if (!response.ok) {
    throw createHttpRequestError({
      message: `${failureLabel} failed with status ${response.status}`,
      statusCode: response.status,
    });
  }

  return response.json();
}
